import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import SkipNextIcon from '@material-ui/icons/SkipNext'
import LinkIcon from '@material-ui/icons/Link'
import BlockIcon from '@material-ui/icons/Block'
import ZoomInIcon from '@material-ui/icons/ZoomIn'
import ZoomOutIcon from '@material-ui/icons/ZoomOut'
import axios from 'axios'

const PAGE_SIZE = 25

function scoreBadgeColor(score) {
  if (score >= 0.85) return '#4caf50'
  if (score >= 0.50) return '#ff9800'
  return '#f44336'
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1)
  },
  counter: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  filterField: {
    minWidth: 200
  },
  mono: {
    fontFamily: 'monospace'
  },
  scoreBadge: {
    color: '#fff',
    fontWeight: 600,
    marginLeft: theme.spacing(1)
  },
  noSuggestion: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic'
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6)
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  expandedCell: {
    paddingBottom: 0,
    paddingTop: 0
  },
  expandedContent: {
    padding: theme.spacing(2)
  },
  suggestionItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' }
  },
  suggestionInfo: {
    flex: 1,
    minWidth: 0,
    marginRight: theme.spacing(1)
  },
  suggestionName: {
    fontSize: '0.9rem',
    fontWeight: 500
  },
  suggestionUnit: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  }
}))

function ZoomablePageImage({ catalogId, pageNumber }) {
  const [zoom, setZoom] = useState(1)
  const [imgError, setImgError] = useState(false)

  if (!catalogId || pageNumber == null) {
    return <Typography variant="body2" color="textSecondary">No catalog page available</Typography>
  }

  if (imgError) {
    return <Typography variant="body2" color="textSecondary">Catalog page image not available</Typography>
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
        <Typography variant="subtitle2">Catalog Page {pageNumber}</Typography>
        <IconButton size="small" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} disabled={zoom <= 0.5}>
          <ZoomOutIcon fontSize="small" />
        </IconButton>
        <Typography variant="caption">{Math.round(zoom * 100)}%</Typography>
        <IconButton size="small" onClick={() => setZoom(z => Math.min(3, z + 0.25))} disabled={zoom >= 3}>
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box style={{ overflow: 'auto', maxHeight: 550, border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#fafafa' }}>
        <img
          src={`/api/vendor-catalogs/${catalogId}/page-image/${pageNumber}`}
          alt={`Page ${pageNumber}`}
          onError={() => setImgError(true)}
          style={{
            width: `${zoom * 100}%`,
            maxWidth: 'none',
            display: 'block',
            transformOrigin: 'top left',
            transition: 'width 0.2s ease'
          }}
        />
      </Box>
    </Box>
  )
}

export default function ReviewQueue({ onBack }) {
  const classes = useStyles()

  const [products, setProducts] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(null)
  const [expandedId, setExpandedId] = useState(null)

  const [categories, setCategories] = useState([])
  const [categoryFilter, setCategoryFilter] = useState('')
  const [manufacturerFilter, setManufacturerFilter] = useState('')

  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const searchDebounceRef = useRef(null)

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  const fetchQueue = useCallback(async () => {
    setLoading(true)
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE }
      if (categoryFilter) params.category = categoryFilter
      if (manufacturerFilter) params.manufacturer = manufacturerFilter
      const res = await axios.get('/api/vendor-catalogs/review-queue', { params })
      setProducts(res.data.products || [])
      setTotal(res.data.total || 0)
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load review queue', severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [page, categoryFilter, manufacturerFilter])

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get('/api/vendor-catalogs/products/categories')
      setCategories(res.data || [])
    } catch {
      // silently ignore
    }
  }, [])

  useEffect(() => { fetchCategories() }, [fetchCategories])
  useEffect(() => { fetchQueue() }, [fetchQueue])
  useEffect(() => { setPage(0) }, [categoryFilter, manufacturerFilter])

  // Debounced Aspire catalog search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    if (!searchInput || searchInput.length < 2) {
      setSearchResults([])
      return
    }
    searchDebounceRef.current = setTimeout(() => {
      fetch(`/api/bid-estimation/catalog?search=${encodeURIComponent(searchInput)}&limit=20`)
        .then(res => res.json())
        .then(data => { if (Array.isArray(data)) setSearchResults(data); else setSearchResults([]) })
        .catch(() => setSearchResults([]))
    }, 350)
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current) }
  }, [searchInput])

  const handleMap = async (productId, aspireItemId, matchedBy) => {
    setActionLoading(productId)
    try {
      await axios.post(`/api/vendor-catalogs/products/${productId}/map`, { aspireItemId, matchedBy })
      setSnackbar({ open: true, message: 'Product mapped', severity: 'success' })
      setExpandedId(null)
      fetchQueue()
    } catch (err) {
      const msg = err.response?.data?.error || 'Map failed'
      setSnackbar({ open: true, message: msg, severity: 'error' })
    } finally {
      setActionLoading(null)
    }
  }

  const handleIgnore = async (productId) => {
    setActionLoading(productId)
    try {
      await axios.post(`/api/vendor-catalogs/products/${productId}/ignore`)
      setSnackbar({ open: true, message: 'Product ignored', severity: 'success' })
      setExpandedId(null)
      fetchQueue()
    } catch (err) {
      const msg = err.response?.data?.error || 'Ignore failed'
      setSnackbar({ open: true, message: msg, severity: 'error' })
    } finally {
      setActionLoading(null)
    }
  }

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id)
    setSearchInput('')
    setSearchResults([])
  }

  const handleSkip = (productId) => {
    // Find next product in the list and expand it
    const idx = products.findIndex(p => p._id === productId)
    if (idx >= 0 && idx < products.length - 1) {
      const nextId = products[idx + 1]._id
      setExpandedId(nextId)
    } else {
      setExpandedId(null)
    }
    setSearchInput('')
    setSearchResults([])
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <IconButton onClick={onBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Review Queue</Typography>
        <Typography variant="body2" className={classes.counter}>
          {total} unmapped product{total !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Filters */}
      <Box className={classes.filters}>
        <TextField
          select
          label="Category"
          variant="outlined"
          size="small"
          className={classes.filterField}
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <MenuItem value="">All Categories</MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Manufacturer"
          variant="outlined"
          size="small"
          className={classes.filterField}
          value={manufacturerFilter}
          onChange={(e) => setManufacturerFilter(e.target.value)}
          placeholder="Filter by manufacturer"
        />
      </Box>

      {/* Loading */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : products.length === 0 ? (
        <Paper className={classes.emptyState}>
          <Typography variant="h6" color="textSecondary">
            All products have been mapped or ignored
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product Name</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Page #</TableCell>
                  <TableCell>Top Suggestion</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => {
                  const topSuggestion = product.suggestions && product.suggestions.length > 0
                    ? product.suggestions[0]
                    : null
                  const isActioning = actionLoading === product._id
                  const isExpanded = expandedId === product._id

                  return (
                    <React.Fragment key={product._id}>
                      <TableRow
                        className={classes.clickableRow}
                        onClick={() => toggleExpand(product._id)}
                        selected={isExpanded}
                      >
                        <TableCell>{product.productName} ({product?.shortDescription})</TableCell>
                        <TableCell className={classes.mono}>{product.vendorItemNumber || '-'}</TableCell>
                        <TableCell>{product.category || '-'}</TableCell>
                        <TableCell align="right">{product.pageNumber || '-'}</TableCell>
                        <TableCell>
                          {topSuggestion ? (
                            <Box display="flex" alignItems="center">
                              <Typography variant="body2">{topSuggestion.itemName}</Typography>
                              <Chip
                                label={Math.round(topSuggestion.score * 100) + '%'}
                                size="small"
                                className={classes.scoreBadge}
                                style={{ backgroundColor: scoreBadgeColor(topSuggestion.score) }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="body2" className={classes.noSuggestion}>
                              No suggestions
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={e => e.stopPropagation()}>
                          {isActioning ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Box display="flex" justifyContent="center" style={{ gap: 4 }}>
                              {topSuggestion && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  style={{ backgroundColor: '#4caf50', color: '#fff' }}
                                  onClick={() => handleMap(product._id, topSuggestion.aspireItemId, topSuggestion.matchType)}
                                >
                                  Map
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleIgnore(product._id)}
                              >
                                Ignore
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded Detail Row */}
                      <TableRow>
                        <TableCell colSpan={6} className={classes.expandedCell}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box className={classes.expandedContent}>
                              <Grid container spacing={3}>
                                {/* Left: Catalog page image with zoom */}
                                <Grid item xs={12} md={6}>
                                  <ZoomablePageImage
                                    catalogId={product.vendorCatalogId}
                                    pageNumber={product.pageNumber}
                                  />
                                </Grid>

                                {/* Right: Suggestions + Search */}
                                <Grid item xs={12} md={6}>
                                  {/* All suggestions */}
                                  <Typography variant="subtitle2" gutterBottom>
                                    Suggested Matches ({product.suggestions ? product.suggestions.length : 0})
                                  </Typography>
                                  {product.suggestions && product.suggestions.length > 0 ? (
                                    product.suggestions.map((s, i) => (
                                      <div key={i} className={classes.suggestionItem}>
                                        <div className={classes.suggestionInfo}>
                                          <Typography className={classes.suggestionName}>
                                            {s.itemName}
                                            {s.allocationUnitType ? <span style={{ color: '#1976d2', fontWeight: 600, marginLeft: 6, fontSize: '0.8rem' }}>#{s.allocationUnitType}</span> : null}
                                            {s.itemCode ? <span style={{ color: '#666', fontWeight: 400, marginLeft: 6, fontSize: '0.8rem' }}>{s.itemCode}</span> : null}
                                          </Typography>
                                          {s.itemAlternateName && s.itemAlternateName !== s.itemName && (
                                            <Typography style={{ fontSize: '0.8rem', color: '#555', fontStyle: 'italic' }}>
                                              {s.itemAlternateName}
                                            </Typography>
                                          )}
                                          <Typography className={classes.suggestionUnit}>
                                            {[s.unitTypeName, s.categoryName, s.cost != null ? `$${Number(s.cost).toFixed(2)}` : null, s.matchType].filter(Boolean).join(' | ')}
                                          </Typography>
                                        </div>
                                        <Chip
                                          label={`${Math.round(s.score * 100)}%`}
                                          size="small"
                                          style={{
                                            backgroundColor: scoreBadgeColor(s.score),
                                            color: '#fff',
                                            fontWeight: 600,
                                            marginRight: 8
                                          }}
                                        />
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="primary"
                                          startIcon={<LinkIcon />}
                                          onClick={() => handleMap(product._id, s.aspireItemId, s.matchType)}
                                          disabled={!!actionLoading}
                                        >
                                          Map
                                        </Button>
                                      </div>
                                    ))
                                  ) : (
                                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                                      No automatic suggestions
                                    </Typography>
                                  )}

                                  {/* Manual Aspire search */}
                                  <Box mt={2}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Search Aspire Catalog
                                    </Typography>
                                    <Autocomplete
                                      options={searchResults}
                                      getOptionLabel={(option) =>
                                        option.itemName
                                          ? `${option.itemName}${option.allocationUnitType ? ' #' + option.allocationUnitType : option.unitTypeName ? ' (' + option.unitTypeName + ')' : ''}`
                                          : ''
                                      }
                                      renderOption={(option) => (
                                        <div>
                                          <Typography style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                            {option.itemName}
                                            {option.allocationUnitType ? <span style={{ color: '#1976d2', fontWeight: 600, marginLeft: 6, fontSize: '0.8rem' }}>#{option.allocationUnitType}</span> : null}
                                            {option.itemCode ? <span style={{ color: '#666', fontWeight: 400, marginLeft: 6, fontSize: '0.8rem' }}>{option.itemCode}</span> : null}
                                          </Typography>
                                          {option.itemAlternateName && option.itemAlternateName !== option.itemName && (
                                            <Typography style={{ fontSize: '0.78rem', color: '#555', fontStyle: 'italic' }}>
                                              {option.itemAlternateName}
                                            </Typography>
                                          )}
                                          <Typography style={{ fontSize: '0.75rem', color: '#888' }}>
                                            {[option.unitTypeName, option.categoryName, option.cost != null ? `$${Number(option.cost).toFixed(2)}` : null].filter(Boolean).join(' | ')}
                                          </Typography>
                                        </div>
                                      )}
                                      onInputChange={(_, value) => setSearchInput(value)}
                                      onChange={(_, selected) => {
                                        if (selected && selected.aspireItemId) {
                                          handleMap(product._id, selected.aspireItemId, 'manual')
                                        }
                                      }}
                                      noOptionsText={searchInput.length < 2 ? 'Type to search...' : 'No items found'}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          variant="outlined"
                                          size="small"
                                          placeholder="Type item name or code..."
                                        />
                                      )}
                                    />
                                  </Box>

                                  {/* Skip / Ignore */}
                                  <Box mt={2} display="flex" style={{ gap: 8 }}>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<SkipNextIcon />}
                                      onClick={() => handleSkip(product._id)}
                                    >
                                      Skip
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<BlockIcon />}
                                      onClick={() => handleIgnore(product._id)}
                                      disabled={!!actionLoading}
                                    >
                                      Ignore
                                    </Button>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onChangePage={(e, newPage) => setPage(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
